import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  ConditionFeatureFlag,
  ConditionStatementColumn,
  ConditionStatementCurrentRow,
  ConditionStatementSingleCell,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  getLookupDestinationTableColumn,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  TableAdapter,
} from '@makyoapp/makyo-common'
import { ArrowShortRight16, Column16, SingleCell16, Table16 } from '@makyoapp/makyo-icon/icon'
import { Button, DD, SimpleInput, Switch, Tip, useTip } from '@makyoapp/makyo-ui'

import { filterFnReturnTypes } from '@/core/db/functions/helpers'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { Help } from '@/components/Help'

import { getOperatorDefaultOption } from '../../shared/condition'
import { ColumnTypeIcon } from '../ColumnTypeIcon'
import { RenderColumn } from '../RenderColumn'
import { VariableColumnDd } from '../VariableSelectV2/VariableColumnDd'
import { VariableTable } from '../VariableSelectV2/VariableTable'
import { VariableWrapper } from '../VariableSelectV2/VariableWrapper'
import { useConditionRepeaterContext } from './ConditionRepeaterContext'
import { useConditionSelectContext } from './ConditionSelectContext'

export function ConditionColumnDataDD({
  type = 'component-data',
  tableId,
}: {
  type?: 'single-cell' | 'component-data' | 'details-page' | 'this-data'
  tableId?: string | null
}) {
  const { t } = useTranslation()

  const { id, setOpen, isRhs, index, isEnd } = useConditionSelectContext()

  const { dbId, tables } = useDbClientContext()
  const { activeLayerTableId } = useActiveLayerContext()

  const { onChange, value } = useConditionRepeaterContext()
  const [show, setShow] = useBoolean(false)

  const isSingle = type === 'single-cell'
  const isCurrent = type === 'details-page' && activeLayerTableId
  const key = isRhs ? 'rhs' : 'lhs'

  const stt = value.conditions[index]?.[key] as ConditionStatementColumn | ConditionStatementSingleCell | undefined

  const tblKey = isEnd && isRhs ? 'endTbl' : 'tbl'
  const colKey = isEnd && isRhs ? 'endCol' : 'col'
  const usKey = isEnd && isRhs ? 'endUserSpecific' : 'userSpecific'
  const rowKey = isEnd && isRhs ? 'endRow' : 'row'

  let effectiveTableId = stt?.[tblKey]

  if (type === 'this-data' && tableId) {
    effectiveTableId = tableId
  }

  if (isCurrent) {
    effectiveTableId = activeLayerTableId
  }

  const [tbl, setTbl] = useState<string | undefined>(effectiveTableId)
  const [col, setCol] = useState<string | undefined>(stt?.[colKey])
  const [us, setUs] = useState(stt?.[usKey] ?? false)
  const [row, setRow] = useState((stt as ConditionStatementSingleCell)?.[rowKey] || '1')

  const tblOptions = useMemo(() => {
    return [{ id: '', name: 'Select Table' } as TableAdapter, ...tables]
  }, [tables])

  const selectedTable = useMemo(() => {
    return tables.find((tb) => tb.id === tbl)
  }, [tables, tbl])

  const colOptions = useMemo(() => {
    let cols = selectedTable?.columns.filter((c) => ![ColumnTypes.Connection].includes(c.type)) ?? []

    const hasLhsColData =
      isRhs &&
      [ConditionFeatureFlag.ColumnData, ConditionFeatureFlag.CurrentRow].includes(value.conditions[index].lhs.flag)

    if (hasLhsColData) {
      const lhsTbl = tables.find((tb) => tb.id === (value.conditions[index].lhs as ConditionStatementColumn)?.tbl)

      let lhsCol = lhsTbl?.columns.find((c) => c.id === (value.conditions[index].lhs as ConditionStatementColumn)?.col)

      if (lhsTbl && lhsCol?.type === ColumnTypes.Lookup) {
        lhsCol = getLookupDestinationTableColumn(lhsCol, lhsTbl, tables)?.fCol
      }

      if (lhsTbl && lhsCol) {
        cols = cols.filter((c) => {
          if (c.type === ColumnTypes.Function) {
            return filterFnReturnTypes(c, [lhsCol?.type], true)
          }
          return c.type === lhsCol?.type
        })
      }
    }

    return [
      { id: '', name: 'Select Column' } as ColumnAdapter,
      { id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter,
      ...cols,
    ]
  }, [selectedTable?.columns, isRhs, value.conditions, index, tables])

  const btnState = isSingle ? !dbId || !tbl || !col || !row : !dbId || !tbl || !col

  const infoMap = {
    'component-data': {
      title: <Trans>Component Data</Trans>,
      icon: <Column16 width={14} height={14} />,
      varId: `${id}-cd-table`,
    },
    'details-page': {
      title: <Trans>Details Page</Trans>,
      icon: <Column16 width={14} height={14} />,
      varId: `${id}-cr-table`,
    },
    'single-cell': {
      title: <Trans>Single Cell</Trans>,
      icon: <SingleCell16 width={14} height={14} />,
      varId: `${id}-sc-table`,
    },
    'this-data': {
      title: <Trans>This Item</Trans>,
      icon: <Column16 width={14} height={14} />,
      varId: `${id}-td-table`,
    },
  }

  const { title, icon, varId } = infoMap[type]

  return (
    <VariableWrapper id={id ? varId : undefined} show={show} setShow={setShow} title={title} icon={icon}>
      <div className='flex-c flex-col'>
        {/*<VariableDb id={id ? `${id}-db` : undefined} db={db} setDb={setDb} />*/}
        {!isCurrent && (
          <VariableTable
            disabled={type === 'this-data'}
            id={id ? `${id}-${isSingle ? 'sc' : 'cd'}-table` : undefined}
            tbl={tbl}
            setTbl={setTbl}
            tblOptions={tblOptions}
          />
        )}
        <VariableColumnDd
          id={id ? `${id}-${isSingle ? 'sc' : 'cd'}-column` : undefined}
          setCol={setCol}
          col={col}
          colOptions={colOptions}
        />
        {isSingle && (
          <div className='flex-c px-2 w-full'>
            <SimpleInput
              type='text'
              id={id ? `${id}-${isSingle ? 'sc' : 'cd'}-row` : undefined}
              inputClassName='min-w-0'
              value={row}
              placeholder={t('Enter Row Number')}
              onChange={(e) => {
                const val = e.currentTarget.value
                if (val === '-' || val === '') {
                  setRow(val)
                } else {
                  const numVal = Number(val)
                  if (!isNaN(numVal)) {
                    setRow(numVal.toString())
                  }
                }
              }}
            />
            <Help>
              <Trans>
                <strong>Row Number:</strong> Use negative number to start from last row. eq: 1 equals first row, -1
                equals last row.
              </Trans>
            </Help>
          </div>
        )}
        {/* <label className='px-2 py-2 w-full flex-c justify-between cursor-pointer'>
          <div>
            <Trans>Use user-specific data</Trans>
          </div>
          <Switch checked={us} onClick={() => setUs(!us)} />
        </label> */}
        <div className='w-full px-2'>
          <Button
            className='w-full'
            disabled={btnState}
            id={id ? `${id}-${isSingle ? 'sc' : 'cd'}-btn` : undefined}
            onClick={() => {
              const nextCond = [...value.conditions]
              const isSame = !isRhs
                ? (nextCond[index].lhs as ConditionStatementColumn)?.tbl === tbl &&
                  (nextCond[index].lhs as ConditionStatementColumn)?.col === col
                : true
              const newColObj = colOptions.find((c) => c.id === col)
              const newTblObj = tblOptions.find((tb) => tb.id === tbl)
              const nextOp = isSame
                ? nextCond[index].operator
                : newColObj && newTblObj
                  ? getOperatorDefaultOption(newColObj, newTblObj, tables)
                  : ConditionTextOperator.IsNotEmpty

              let flag = ConditionFeatureFlag.ColumnData
              if (isCurrent) {
                flag = ConditionFeatureFlag.CurrentRow
              }
              if (isSingle) {
                flag = ConditionFeatureFlag.SingleCell
              }

              nextCond[index] = {
                ...nextCond[index],
                [key]: {
                  ...nextCond[index][key],
                  flag,
                  isRange:
                    isRhs && [...NUMERIC_OPERATOR_WITH_RANGE_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS].includes(nextOp),
                  [tblKey]: tbl ?? '',
                  [colKey]: col ?? '',
                  [usKey]: us,
                } as unknown as ConditionStatementColumn | ConditionStatementSingleCell | ConditionStatementCurrentRow,
                operator: nextOp,
              }

              if (isSingle && nextCond[index]?.[key]) {
                ;(nextCond[index][key] as ConditionStatementSingleCell)[rowKey] = row
              }

              onChange?.({
                isAny: value.isAny,
                conditions: nextCond,
              })
              setShow(false)
              setOpen(false)
            }}
          >
            <Trans>Select</Trans>
          </Button>
        </div>
      </div>
    </VariableWrapper>
  )
}

export function ConditionColumnStatement({
  statement,
}: {
  statement: ConditionStatementColumn | ConditionStatementSingleCell | ConditionStatementCurrentRow
}) {
  const { activeLayerTableId } = useActiveLayerContext()
  const { tables: tblOptions } = useDbClientContext()
  const { isEnd } = useConditionSelectContext()
  const tblKey = isEnd ? 'endTbl' : 'tbl'
  const colKey = isEnd ? 'endCol' : 'col'
  const isCurrent = statement.flag === ConditionFeatureFlag.CurrentRow
  const tbl = isCurrent ? activeLayerTableId : statement[tblKey]
  const col = statement[colKey]

  const [tblObj, colObj] = useMemo(() => {
    const selTbl = tblOptions.find((tb) => tb.id === tbl)
    if (selTbl) {
      let columns = [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter, ...selTbl.columns]
      return [selTbl, columns.find((c) => c.id === col)]
    }
    return [selTbl, undefined]
  }, [tblOptions, tbl, col])

  const { t } = useTranslation()

  let icon: any = <Column16 width={14} height={14} />
  let title = t('Component Data')
  if (statement.flag === ConditionFeatureFlag.SingleCell) {
    icon = <SingleCell16 width={14} height={14} />
    title = t('Single Cell')
  }
  if (statement.flag === ConditionFeatureFlag.CurrentRow) {
    title = t('Details Page')
  }

  const {
    x,
    y,
    strategy,
    context,
    isOpen: isTipOpen,
    placement,
    arrowRef,
    getFloatingProps,
    refs,
    getReferenceProps,
  } = useTip({
    placement: 'top',
  })

  return (
    <>
      <div className='flex-c min-w-0' ref={refs.setReference} {...getReferenceProps()}>
        <div className='text-m-ss uppercase' title={title}>
          {icon}
        </div>
        <RenderColumn col={colObj} className='min-w-0' textClassName='txt' width={14} height={14} withStyle />
      </div>

      {isTipOpen && (
        <Tip
          className='text-m-sm'
          x={x}
          y={y}
          placement={placement}
          strategy={strategy}
          ref={refs.setFloating}
          {...getFloatingProps()}
          context={context}
          arrowRef={arrowRef}
        >
          <div className='flex-c'>
            <div className='flex-c whitespace-nowrap'>
              {icon}
              <Trans>{title}</Trans>
            </div>
            <div className='flex-c'>
              {tblObj && (
                <>
                  <ArrowShortRight16 />
                  <div className='flex-c whitespace-nowrap'>
                    <Table16 width={14} height={14} />
                    {tblObj.name}
                  </div>
                </>
              )}

              {colObj && (
                <>
                  <ArrowShortRight16 />
                  <div className='flex-c whitespace-nowrap'>
                    <ColumnTypeIcon type={colObj.type} size={14} />
                    {colObj.name}
                  </div>
                </>
              )}
            </div>
          </div>
        </Tip>
      )}
    </>
  )
}
